import { useConfig } from 'vike-react/useConfig';
import { useEffect, useMemo } from 'react';

const SITE_NAME = import.meta.env.VITE_SITE_NAME?.toString() || '医療法人文正会小林歯科医院';
const SITE_URL = 'https://www.kobayashi-dental.com';
const OG_IMAGE_URL = `${SITE_URL}/img/ogp.jpg`;
const DEFAULT_DESCRIPTION =
  '長野県東御市の小林歯科医院です。診療案内、医院情報、アクセス、予約方法などをご案内します。';

const toCanonicalUrl = (pathname: string) => {
  const normalizedPath = pathname === '/' ? '' : pathname.replace(/^\/+|\/+$/g, '');
  return `${SITE_URL}/${normalizedPath}${normalizedPath ? '/' : ''}`;
};

const setMetaTag = (name: string, content: string, attribute: 'name' | 'property' = 'name') => {
  let meta = document.head.querySelector<HTMLMetaElement>(`meta[${attribute}="${name}"]`);

  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute(attribute, name);
    document.head.append(meta);
  }

  meta.content = content;
};

const setCanonicalLink = (url: string) => {
  let link = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');

  if (!link) {
    link = document.createElement('link');
    link.rel = 'canonical';
    document.head.append(link);
  }

  link.href = url;
};

export const usePageMeta = (
  pageTitle: string | undefined,
  pageDescription: string | undefined,
  pathname: string
) => {
  const config = useConfig();
  const fullTitle = pageTitle ? `${pageTitle} | ${SITE_NAME}` : SITE_NAME;
  const description = pageDescription || DEFAULT_DESCRIPTION;
  const canonicalUrl = toCanonicalUrl(pathname);

  const head = useMemo(
    () => (
      <>
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content={SITE_NAME} />
        <meta property="og:url" content={canonicalUrl} />
        <meta name="twitter:title" content={fullTitle} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={OG_IMAGE_URL} />
        <meta name="twitter:url" content={canonicalUrl} />
        <link rel="canonical" href={canonicalUrl} />
      </>
    ),
    [canonicalUrl, description, fullTitle]
  );

  config({
    title: fullTitle,
    description,
    image: OG_IMAGE_URL,
    Head: head,
  });

  useEffect(() => {
    document.title = fullTitle;
    setMetaTag('og:type', 'website', 'property');
    setMetaTag('og:site_name', SITE_NAME, 'property');
    setMetaTag('og:url', canonicalUrl, 'property');
    setMetaTag('twitter:title', fullTitle);
    setMetaTag('twitter:description', description);
    setMetaTag('twitter:image', OG_IMAGE_URL);
    setMetaTag('twitter:url', canonicalUrl);
    setCanonicalLink(canonicalUrl);
  }, [canonicalUrl, description, fullTitle]);
};
